import { AppData, Lightning, Registry } from '@lightningjs/sdk';
import Page from 'components/Page';
import { PageId } from 'types/pageId';
import { Channel, Episode, Show, Video } from 'types/api/media';
import { translate } from 'support/translate';
import { InputChangedData } from 'types/lightning';
import SearchInputField from 'components/common/input/SearchInputField';
import Keyboard from 'components/common/input/Keyboard';
import { constants } from 'aliases';
import EmptySearchResults from 'components/pages/search/EmptySearchResults';
import { getSearch } from 'services/tivoService';
import { SelectItemContext } from 'types/events';
import { StaticViewContexts } from 'types/analytics';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';
import SearchResults, {
  SearchResultTabTypes,
} from 'components/pages/search/SearchResults';
import LandingResults from 'components/pages/search/LandingResults';
import { BOUNCY_SCROLL } from 'support/animations';
import { debounceWithCancel } from 'support/generalUtils';
import { KeyboardConfigWithClear } from 'components/common/input/KeyboardConfiguration';
import { Swimlanes } from 'services/cwData';
import { TileParentContext } from 'components/common/tiles/Tile';

const SPINNER_POSITION_CENTER = {
  x: 960,
  y: 540,
};

const NAVBAR_COLLAPSED_WIDTH = constants.sizing.navbar.collapsedWidth;

const SEARCH_BAR_X = NAVBAR_COLLAPSED_WIDTH;

const SEARCH_BAR_Y = 120;
const SEARCH_BAR_WIDTH = 1678;

const KEYBOARD_X = NAVBAR_COLLAPSED_WIDTH;
const KEYBOARD_WIDTH = 1920 - KEYBOARD_X;

const EMPTY_SEARCH_RESULTS_X = NAVBAR_COLLAPSED_WIDTH + 533;
const EMPTY_SEARCH_RESULTS_Y = 341;

const MIN_SEARCH_TERM_LEN = 2;

const SEARCH_DEBOUNCE_DELAY = 3500;

export interface SearchPageTemplateSpec
  extends Lightning.Component.TemplateSpec {
  pageData: {
    exploreData: Swimlanes | null | undefined;
    trendingData: (Show | Video)[];
    query: string | undefined;
    reset: boolean;
  };

  Content: {
    SearchBar: typeof SearchInputField;
    LandingResults: typeof LandingResults;
    SearchResults: typeof SearchResults;
    EmptySearchResults: typeof EmptySearchResults;
  };
  Keyboard: typeof Keyboard;
}

enum SearchState {
  NothingSearched = 'NothingSearched',
  NoSearchResults = 'NoSearchResults',
  SearchResultsFound = 'SearchResultsFound',
}

export default class SearchPage
  extends Page<SearchPageTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<SearchPageTemplateSpec>
{
  protected override _pageId = PageId.SEARCH;
  protected override _viewContext = StaticViewContexts.SEARCH_PAGE;

  private _searchData: (Channel | Episode | Show)[] = [];

  private _Content = this.getByRef('Content')!;
  private _SearchBar = this._Content.getByRef('SearchBar')!;

  private _LandingResults = this._Content.getByRef('LandingResults')!;

  private _SearchResults = this._Content.getByRef('SearchResults')!;

  private _EmptySearchResults = this._Content.getByRef('EmptySearchResults')!;

  private _Keyboard = this.getByRef('Keyboard')!;

  private _selectedCrossIndex?: number = undefined;
  private _selectedMainIndex?: number = undefined;

  private searchState: SearchState = SearchState.NothingSearched;
  private searchQueryId = '';
  private tabType: SearchResultTabTypes = 'TopResults';

  private debouncer = debounceWithCancel(
    this.fetchSearchResults.bind(this),
    SEARCH_DEBOUNCE_DELAY,
  );

  get announce() {
    return translate('ttsPrompts.search');
  }

  static override _template(): Lightning.Component.Template<SearchPageTemplateSpec> {
    return {
      ...super._template(),
      Content: {
        SearchBar: {
          type: SearchInputField,
          x: SEARCH_BAR_X,
          y: SEARCH_BAR_Y,
          w: SEARCH_BAR_WIDTH,
          signals: {
            $onHover: '$onPageHover',
          },
        },
        LandingResults: {
          visible: false,
          type: LandingResults,
          signals: {
            $onHover: '$onPageHover',
            $shouldScrollDown: '$shouldScrollDown',
            $focusSearchBar: '$focusSearchBar',
          },
        },
        SearchResults: {
          visible: false,
          type: SearchResults,
          signals: {
            $onHover: '$onPageHover',
          },
        },
        EmptySearchResults: {
          type: EmptySearchResults,
          visible: false,
          x: EMPTY_SEARCH_RESULTS_X,
          y: EMPTY_SEARCH_RESULTS_Y,
        },
      },
      Keyboard: {
        type: Keyboard,
        keyboardConfiguration: KeyboardConfigWithClear,
        x: KEYBOARD_X,
        w: KEYBOARD_WIDTH,
        signals: {
          hideKeyboard: '_onHideKeyboard',
          inputChanged: '_onSearchInputChanged',
          inputComplete: '_onSearch',
          $onHover: true,
        },
        zIndex: constants.ui.zIndex.foregroundLayer + 1,
      },
    };
  }

  override _setup() {
    super._setup();
    this._setState('SearchBar');
  }

  override _onDataProvided() {
    const { query, reset } = this.pageData;

    if (reset) {
      this._setState('SearchBar');
      this.resetComponent();
      this._SearchBar.inputField._input = '';
    }

    this.isNavBarVisible = true;
    const inputField = this._SearchBar.inputField;
    const searchInput = inputField._input;

    if (query) {
      // Search for the query provided
      inputField.onInputChanged({ input: query });

      this.fetchSearchResults(query);
      this._setState('SearchBar');
    } else if (searchInput) {
      // Do nothing
    } else {
      // Show default
      this.showLandingResults();
    }
  }

  override $onTileSelected(
    data?: Channel | Episode | Show,
    context?: SelectItemContext<TileParentContext>,
  ) {
    if (data && this.searchState === SearchState.SearchResultsFound) {
      AppData?.recentSearchesService.add(data);
    }

    this._selectedMainIndex = context?.parentContext?.mainIndex;
    this._selectedCrossIndex = context?.parentContext?.crossIndex;

    this.debouncer.cancel();
    this._SearchBar.inputField.onInputChanged({ input: this.searchQueryId });
    super.$onTileSelected(data, context);
  }

  private resetComponent() {
    this._searchData = [];
    this._LandingResults.resetComponent();
    this._SearchResults.resetComponent();
    this.spinnerPosition = SPINNER_POSITION_CENTER;
  }

  private showSearchResults() {
    this.searchState = SearchState.SearchResultsFound;

    this._SearchResults.searchData = this._searchData;
    this._SearchResults.showSearchResults();

    this._LandingResults.patch({ visible: false });
    this._SearchResults.patch({ visible: true });
    this._EmptySearchResults.patch({ visible: false });
  }

  private showLandingResults() {
    this.searchState = SearchState.NothingSearched;

    const { exploreData, trendingData } = this.pageData;
    this._LandingResults.showLandingResults(exploreData, trendingData);

    if (
      this._selectedMainIndex !== undefined &&
      this._selectedCrossIndex !== undefined
    ) {
      this._LandingResults.setIndices(
        this._selectedMainIndex,
        this._selectedCrossIndex,
      );

      this._selectedMainIndex = undefined;
      this._selectedCrossIndex = undefined;
    }

    this._LandingResults.patch({ visible: true });
    this._SearchResults.patch({ visible: false });
    this._EmptySearchResults.patch({ visible: false });
  }

  private showEmptyResults(searchQueryId: string) {
    this.searchState = SearchState.NoSearchResults;

    this._SearchResults.clear();
    const currentState = this._getState();
    if (!(currentState === 'SearchBar' || currentState === 'Keyboard')) {
      this._setState('SearchBar');
    }

    this._SearchResults.hideResults();
    this._LandingResults.patch({ visible: false });
    this._EmptySearchResults.patch({
      visible: true,
      search: searchQueryId,
    });

    // setting the focus on the search bar cancels this TTS command
    // delay this TTS so it will be appended after the focus change
    Registry.setTimeout(() => {
      this._EmptySearchResults.fireAnnounce();
    }, 1000);
  }

  private initializeSearchResults(
    searchQueryId?: string,
    tabType?: SearchResultTabTypes,
  ) {
    // Do not update the items if the search term or tab has changed
    if (searchQueryId !== this.searchQueryId || tabType !== this.tabType)
      return;

    this._SearchResults.initializeSearchResults(
      searchQueryId,
      tabType,
      this.searchQueryId,
      this.tabType,
    );
  }

  private async fetchSearchResults(searchQueryId: string) {
    const isSameSearch =
      this.searchQueryId === searchQueryId ||
      (searchQueryId.length < MIN_SEARCH_TERM_LEN &&
        this.searchQueryId.length < MIN_SEARCH_TERM_LEN);

    if (isSameSearch) {
      this.loaded = true;
      return;
    }

    this._SearchResults.clear();
    this.resetComponent();

    this.loaded = false;
    this.searchQueryId = searchQueryId;
    this.tabType = 'TopResults'; // The default tab is 'TopResults'

    if (searchQueryId.length < MIN_SEARCH_TERM_LEN) {
      this.showLandingResults();
      this.loaded = true;
      return;
    }

    try {
      const searchItems = await getSearch(searchQueryId);
      if (searchQueryId !== this.searchQueryId) {
        // Do not update the data if the search term has changed
        return;
      } else if (!searchItems.length) {
        // Reset loaded state and show empty results if no identifiers found
        this.showEmptyResults(searchQueryId);
      } else {
        // Order of operations matter
        this._searchData = searchItems;
        this.showSearchResults();
        this.initializeSearchResults(searchQueryId, 'TopResults');
      }
    } catch (e) {
      reportError({ error: e, userFacing: true, code: ErrorCode.c4 });
      this.showEmptyResults(searchQueryId);
    }
    this.loaded = true;
  }

  private _onHideKeyboard() {
    this._setState('SearchBar');
  }

  private async _onSearchInputChanged(event: InputChangedData) {
    const { input } = event;

    if (input.length < MIN_SEARCH_TERM_LEN) {
      this.debouncer.cancel();
      this.fetchSearchResults('');
    } else {
      this.loaded = false;
      this.debouncer.debounce(input);
    }
  }

  override $onPageHover(target: unknown) {
    super.$onPageHover(target);

    switch (target) {
      case this._SearchBar:
        this._setState('SearchBar');
        break;
      case this._LandingResults.linkItems:
        this._setState('LandingResults');
        this._LandingResults._setState('LinkItems');
        break;
      case this._LandingResults.trendingResultsList:
        this._setState('LandingResults');
        this._LandingResults._setState('TrendingResults');
        break;
      case this._LandingResults.recentResultsList:
        this._setState('LandingResults');
        this._LandingResults._setState('RecentResults');
        break;
      case this._SearchResults.searchFilters:
        this._setState('SearchResults');
        this._SearchResults._setState('SearchFilters');
        break;
      case this._SearchResults.searchResultsGrid:
        this._setState('SearchResults');
        this._SearchResults._setState('SearchResultsGrid');
        break;
      case this._Keyboard:
        this._setState('Keyboard');
        break;
    }
  }

  private $shouldScrollDown(scrollDown: boolean) {
    this._SearchBar.setVisibility(!scrollDown);

    this._LandingResults.setSmooth(
      'y',
      scrollDown ? -SEARCH_BAR_Y : 0,
      BOUNCY_SCROLL,
    );
  }

  private $focusSearchBar() {
    this._setState('SearchBar');
  }

  static override _states() {
    return [
      class SearchBar extends this {
        override _getFocused() {
          return this._SearchBar;
        }

        override _handleEnter() {
          this._setState('Keyboard');
        }

        override _captureKey() {
          // Block interaction until searching is complete
          return !this.loaded;
        }

        override _handleDown() {
          if (this._LandingResults.visible) {
            this._setState('LandingResults');
          } else {
            this._setState('SearchResults');
          }
        }
      },
      class Keyboard extends this {
        override $enter() {
          const inputField = this._SearchBar.inputField;
          this._Keyboard.patch({ inputField });
          this._SearchBar.patch({ toggleCursor: true });
        }

        override $exit() {
          this._SearchBar.patch({ toggleCursor: false });
        }

        override _getFocused() {
          return this._Keyboard;
        }

        override _handleUp() {
          this._setState('SearchBar');
        }
      },
      class LandingResults extends this {
        override _getFocused() {
          return this._LandingResults;
        }

        override _handleUp() {
          this._handleBack();
        }

        override _handleBack() {
          this._setState('SearchBar');
        }
      },
      class SearchResults extends this {
        override _getFocused() {
          return this._SearchResults;
        }

        override _handleUp() {
          this._handleBack();
        }

        override _handleBack() {
          this._setState('SearchBar');
        }
      },
    ];
  }
}
